import { useContext, useMemo } from 'react';

import BoardContext from 'contexts/BoardContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import programHasSubPeriods from 'contextProviders/BonusAppProvider/programHasSubPeriods';
import isPerformanceBoard from 'isPerformanceBoard';

import sectionTypeCheckers from '../PerformanceBoardSlideOut/sectionTypeCheckers';
import resolveOverridableRewardName, {
  ELIGIBILITY_FIELD,
} from '../PerformanceBoardSlideOut/Sections/BonusSubPeriodFactTable/overridableRewardName';
import useHasBonusSubPeriods from './useHasBonusSubPeriods';

/**
 * Whether the slideout's override mode should be driven by the per-week fact
 * table instead of the single-record override form. True only when: the board has
 * the setting on, its dataset maps to a sub-period bonus program, the selected
 * period actually carries weekly windows, and at least one fact-table section
 * exposes a pay column that maps to a reward's value field (the overridable
 * column). Otherwise the existing single-record override flow is used.
 */
const useWeeklyFactTableOverride = (): { isWeeklyOverrideBoard: boolean } => {
  const { board } = useContext(BoardContext);
  const { getBonusProgram } = useContext(DatasetDefinitionsContext);
  const hasBonusSubPeriods = useHasBonusSubPeriods();

  const isWeeklyOverrideBoard = useMemo(() => {
    if (!isPerformanceBoard(board) || !board.isWeeklyFactTableOverrideEnabled) {
      return false;
    }
    const program = getBonusProgram(board.dataType);
    if (!program || !programHasSubPeriods(program) || !hasBonusSubPeriods) {
      return false;
    }
    // At least one fact-table section must expose an overridable column: a pay
    // column mapping to a reward, or the eligibility column.
    return board.slideout.some((section) => {
      if (
        sectionTypeCheckers.isBonusRewardSection(section) ||
        sectionTypeCheckers.isBonusTotalRewardSection(section) ||
        sectionTypeCheckers.isBonusEligibilitySection(section)
      ) {
        return (section.factTableFields ?? []).some(
          (f) =>
            f === ELIGIBILITY_FIELD ||
            !!resolveOverridableRewardName({ field: f, program }),
        );
      }
      return false;
    });
  }, [board, getBonusProgram, hasBonusSubPeriods]);

  return { isWeeklyOverrideBoard };
};

export default useWeeklyFactTableOverride;
